import { createContext, useMemo, useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOfWeek, format, addMonths } from 'date-fns';
import { useLazyQuery, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { GET_CARRIERS, PAYABLES, UPDATE_INVOICES } from './graphql';
import { useColumns, QUICKBOOKS_CSV_COLUMNS } from './columns';
import { FILTERS } from './constants';
import { carrierAdminPayableScope } from './utils';
import { UserContext } from '@/components/App';
import { useWeekStartsOn, useCanViewIntegrations } from '@/hooks';
import { buildPayPeriodWeeks, payPeriodWhere, buildCustomRangeWeek } from '@/utilities/payPeriod';
import { invoiceSearchWhere } from '@/utilities/invoiceSearch';
import { generateLineItems } from '../../../Accounting/Invoices/utils';
import csvDownload from 'json-to-csv-export';
import * as ROUTES from '@/constants/routes';
import { ASSET_HEAVY } from '@/constants/featureFlags';

export const Context = createContext();

const TODAY = new Date(new Date().setHours(0, 0, 0, 0));
const lastYr = addMonths(TODAY, -12);
const sow = startOfWeek(lastYr, { weekStartsOn: 1 });

const PAGE_SIZE = 50;

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { admin_clients, client, teammate, user } = useContext(UserContext);
    const weekStartsOn = useWeekStartsOn();
    const [hasMore, setHasMore] = useState(false);
    const [selectedMap, setSelected] = useState({});
    const [filter, setFilter] = useState({
        status: FILTERS.NOT_PAID,
        carriers: [],
    });

    const [isAdmin, isOnwardAdmin, isCarrierAdmin] = useMemo(() => {
        const isAdmin = user?.roles?.['ADMIN'] && !client?.client_id && user?.organization_id;
        const isOnwardAdmin = user?.roles?.['ONWARD_ADMIN'] && !client?.client_id;
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] &&
            client?.user?.circles?.[ASSET_HEAVY] &&
            (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]));
        return [isAdmin, isOnwardAdmin, isCarrierAdmin];
    }, [user, client, teammate]);

    const weeks = useMemo(() => buildPayPeriodWeeks(TODAY, weekStartsOn, 5), [weekStartsOn]);

    // Apply a custom date range from the picker's "Custom Range" option.
    const setCustomRange = useCallback((sow, eow) => {
        const week = buildCustomRangeWeek(sow, eow);
        if (!week) return;
        setFilter((prev) => ({ ...prev, week }));
    }, []);

    const [getInvoices, { data, loading: initInflight, fetchMore }] = useLazyQuery(PAYABLES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getCarriers, { data: carrierData }] = useLazyQuery(GET_CARRIERS, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [updateInvoices, { loading: updateInvoiceInflight }] = useMutation(UPDATE_INVOICES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const carriers = useMemo(() => {
        const carriers =
            (carrierData?.results || [])
                .map((invoice) => {
                    return {
                        label: invoice?.client?.business_name,
                        value: invoice.client_id,
                    };
                })
                .sort((l, r) => l.label?.localeCompare(r.label)) || [];

        return carriers;
    }, [carrierData]);

    const where = useMemo(() => {
        let where = [];

        if (isAdmin) {
            where.push({ type: { _eq: 'PAYABLE' } });
            where.push({ organization_id: { _eq: user.organization_id } });
        } else if (isOnwardAdmin) {
            where.push({ type: { _eq: 'ONWARD_PAYABLE' } });
        } else if (isCarrierAdmin) {
            where.push({ type: { _eq: 'PAYABLE' } });
            where.push(carrierAdminPayableScope(client));
        } else {
            return {};
        }

        switch (filter.status) {
            case FILTERS.DRAFT:
                where.push({ status: { _eq: 'DRAFT' } });
                break;
            case FILTERS.NOT_PAID:
                where.push({ status: { _eq: 'UNPAID' } });
                break;
            case FILTERS.APPROVED:
                where.push({ status: { _eq: 'APPROVED' } });
                break;
            case FILTERS.PAID:
                where.push({ status: { _eq: 'PAID' } });
                break;
            case FILTERS.REVERTED:
                where.push({ status: { _eq: 'REVERTED' } });
                break;
        }

        if (filter?.carriers?.length) {
            where.push({ client_id: { _in: filter?.carriers } });
        }

        if (filter?.week) {
            where.push(...payPeriodWhere(filter.week));
        }

        if (filter?.searchTerm) {
            // A payable's orders hang off one of four leg relationships, and a contractor invoice
            // populates `contractor_orders` with `orders` empty — searching only `orders` never
            // matched those rows.
            where.push(invoiceSearchWhere(filter.searchTerm, ['orders', 'contractor_orders']));
        }

        return { _and: where };
    }, [filter, user, client, isOnwardAdmin, isAdmin, isCarrierAdmin]);

    useEffect(() => {
        if (Object.keys(where).length > 0) {
            getInvoices({
                variables: {
                    where,
                    limit: PAGE_SIZE,
                    offset: 0,
                },
            }).then(({ data }) => {
                setHasMore(data?.results?.length === PAGE_SIZE);
            });
        }
    }, [where, PAGE_SIZE]);

    useEffect(() => {
        let carrierWhere = [];

        if (isOnwardAdmin) {
            carrierWhere.push({ type: { _eq: 'ONWARD_PAYABLE' } });
        } else if (isAdmin) {
            carrierWhere.push({ type: { _eq: 'PAYABLE' } });
            carrierWhere.push({ organization_id: { _eq: user.organization_id } });
        } else if (isCarrierAdmin) {
            carrierWhere.push({ type: { _eq: 'PAYABLE' } });
            carrierWhere.push(carrierAdminPayableScope(client));
        } else {
            return;
        }

        getCarriers({
            variables: {
                where: { _and: carrierWhere },
            },
        });
    }, [isOnwardAdmin, isAdmin, admin_clients, client]);

    const invoices = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const selected = useMemo(() => {
        return Object.keys(selectedMap).filter((attr) => selectedMap[attr]);
    }, [selectedMap]);

    const invoicesMap = useMemo(() => {
        return Object.fromEntries(invoices.map((invoice) => [invoice.carrier_invoice_id, invoice]));
    }, [invoices]);

    const selectedObj = useMemo(() => {
        return selected.map((id) => invoicesMap[id]);
    }, [selected, invoicesMap]);

    const loadMore = useCallback(() => {
        fetchMore({
            variables: {
                where,
                limit: PAGE_SIZE,
                offset: data?.results?.length || 0,
            },
            updateQuery: (data, { fetchMoreResult }) => {
                const prev = Object.fromEntries(data.results.map((invoice) => [invoice.carrier_invoice_id, true]));
                const clone = [
                    ...data.results,
                    ...fetchMoreResult.results.filter((invoice) => !prev[invoice.carrier_invoice_id]),
                ];
                return {
                    results: clone,
                };
            },
        }).then((result) => {
            if (result.data.results.length < PAGE_SIZE) {
                setHasMore(false);
            }
        });
    }, [where, fetchMore, PAGE_SIZE, data]);

    const callbacks = {
        exportCsv: () => {
            const exportData = generateLineItems(selectedObj);
            csvDownload({
                headers: QUICKBOOKS_CSV_COLUMNS.map((col) => col.header),
                filename: `receivables-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    QUICKBOOKS_CSV_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        setFilter,
        setCustomRange,
        loadMore,
        onRowClick: (invoice) => {
            const path = ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', invoice.carrier_invoice_id);
            return navigate(path);
        },
        getRowId: (invoice) => {
            return invoice.carrier_invoice_id;
        },
        setSubmitted: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        setApproved: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        setPaid: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        submitId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        approveId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        payId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        cancelId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'CANCELLED',
                    },
                },
            });
        },
        flagRowToggle: (invoice) => {
            updateInvoices({
                variables: {
                    ids: [invoice.carrier_invoice_id],
                    patch: {
                        flagged: !invoice.flagged,
                    },
                },
            });
        },
        selectRows: setSelected,
    };

    const canViewIntegrations = useCanViewIntegrations();
    const COLUMNS = useColumns({ callbacks, isAdmin: true, canViewIntegrations });

    return (
        <Context.Provider
            value={{
                state: {
                    weeks,
                    hasMore,
                    carriers,
                    selected,
                    invoices,
                    filter,
                    columns: COLUMNS,
                },
                loading: {
                    init: initInflight,
                    update: updateInvoiceInflight,
                    create: false,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
